import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";

import { useToast } from "@/hooks/use-toast";
import { mailStatus } from "@/api/admin-api";
import {
  TP_DETAIL_KEY,
  TP_LIST_KEY,
  getIntegrationDetail,
  putIntegration,
  testIntegration,
} from "@/api/third-party-api";
import { usePatchTestSettings, useTestSettings } from "@/api/tests-api";

const MAILGUN_KEY = "mailgun";
export const MAIL_STATUS_KEY = ["admin", "mail", "status"] as const;

/**
 * Admin → Mail.
 *
 * Two things live here and they are genuinely different: the CREDENTIALS
 * that let this system send anything, and the DECISION about who is told
 * when a candidate finishes. Keeping them on one screen is the point —
 * "why did nobody get the notification" is answered by looking at both
 * at once.
 *
 * The credentials go through the ordinary integrations framework rather
 * than a settings table of their own, so the secret handling comes with
 * them: `secret` fields are masked on read, and `client_visible` stays
 * default-deny, which is what keeps the API key out of every logged-in
 * browser the way MedASR's once was not.
 */
export function MailPanel() {
  const { toast } = useToast();
  const qc = useQueryClient();

  const detail = useQuery({
    queryKey: TP_DETAIL_KEY(MAILGUN_KEY),
    queryFn: () => getIntegrationDetail(MAILGUN_KEY),
  });
  const status = useQuery({ queryKey: MAIL_STATUS_KEY, queryFn: mailStatus });
  const settings = useTestSettings();
  const patchSettings = usePatchTestSettings();

  // What the admin has actually TYPED, keyed by field. Anything absent
  // was left alone and is saved back as the stored raw value — seeding
  // the form from `config_masked` and posting it wholesale would write
  // the bullets over the real API key and destroy it with no error.
  const [edited, setEdited] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [recipients, setRecipients] = useState<string | null>(null);

  useEffect(() => {
    if (enabled === null && detail.data) setEnabled(detail.data.enabled);
  }, [detail.data, enabled]);

  useEffect(() => {
    if (recipients === null && settings.data) {
      setRecipients(settings.data.completion_notify_emails ?? "");
    }
  }, [settings.data, recipients]);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: TP_DETAIL_KEY(MAILGUN_KEY) });
    void qc.invalidateQueries({ queryKey: TP_LIST_KEY });
    void qc.invalidateQueries({ queryKey: MAIL_STATUS_KEY });
  };

  const failure = (err: unknown, fallback: string) =>
    (err as { response?: { data?: { detail?: string } } })?.response?.data
      ?.detail ?? fallback;

  const save = useMutation({
    mutationFn: () => {
      const cfg: Record<string, unknown> = {
        ...(detail.data?.config_raw ?? {}),
      };
      for (const f of detail.data?.schema.fields ?? []) {
        const typed = edited[f.name];
        if (typed === undefined) continue;
        cfg[f.name] = f.type === "boolean" ? typed === "true" : typed;
      }
      return putIntegration(MAILGUN_KEY, {
        config: cfg,
        enabled: enabled ?? false,
      });
    },
    onSuccess: () => {
      setEdited({});
      refresh();
      toast({ title: "Mail settings saved" });
    },
    onError: (err) =>
      toast({ title: failure(err, "Could not save"), variant: "error" }),
  });

  const test = useMutation({
    mutationFn: () => testIntegration(MAILGUN_KEY, null),
    onSuccess: (r) => {
      refresh();
      toast({
        title: r.message,
        ...(r.details ? { description: r.details } : {}),
        variant: r.success ? "success" : "error",
      });
    },
    onError: (err) =>
      toast({ title: failure(err, "Could not test"), variant: "error" }),
  });

  const saveRecipients = async () => {
    try {
      await patchSettings.mutateAsync({
        completion_notify_emails: (recipients ?? "").trim(),
      });
      toast({ title: "Notification list saved" });
    } catch (err) {
      toast({
        title: failure(err, "Could not save the list"),
        variant: "error",
      });
    }
  };

  const schema = detail.data?.schema;
  const available = status.data?.available ?? false;
  const dirty = Object.keys(edited).length > 0;

  const shown = (name: string, type: "string" | "boolean"): string => {
    const typed = edited[name];
    if (typed !== undefined) return typed;
    const v = detail.data?.config_masked[name];
    if (type === "boolean") return v ? "true" : "false";
    return typeof v === "string" ? v : "";
  };

  return (
    <div data-cy="mail-panel" className="flex flex-col gap-6">
      {/* Whether mail actually works, said first. Everything below is
          configuration; this is its consequence. */}
      <section
        data-cy="mail-status"
        className={[
          "flex max-w-2xl items-start gap-3 rounded-xl border px-4 py-3",
          available
            ? "border-status-done/40 bg-status-done-bg"
            : "border-border bg-background",
        ].join(" ")}
      >
        {available ? (
          <CheckCircle2
            aria-hidden
            className="mt-0.5 h-5 w-5 text-status-done"
          />
        ) : (
          <XCircle aria-hidden className="mt-0.5 h-5 w-5 text-foreground-muted" />
        )}
        <div className="text-sm">
          <p className="font-semibold text-foreground">
            {available ? "Mail is being sent" : "No mail is being sent"}
          </p>
          <p className="mt-0.5 text-foreground-muted">
            {available
              ? `Sending as ${status.data?.from_address ?? "the configured address"}.`
              : (status.data?.reason ?? "Checking…")}
          </p>
        </div>
      </section>

      {/* ---- credentials ------------------------------------------- */}
      <section className="max-w-2xl rounded-xl border border-border bg-background-card p-5">
        <h3 className="text-sm font-semibold text-foreground">
          {schema?.display_name ?? "Mailgun"}
        </h3>
        <p className="mt-1 text-xs text-foreground-muted">
          {schema?.description ?? "The account this system sends through."}
        </p>

        {detail.isLoading ? (
          <p className="mt-4 text-sm text-foreground-muted">Loading…</p>
        ) : (
          <div className="mt-4 flex flex-col gap-3">
            {schema?.fields.map((f) =>
              f.type === "boolean" ? (
                <label
                  key={f.name}
                  className="flex items-start gap-2 text-sm text-foreground"
                >
                  <input
                    type="checkbox"
                    data-cy={`mail-field-${f.name}`}
                    checked={shown(f.name, "boolean") === "true"}
                    onChange={(e) =>
                      setEdited((v) => ({
                        ...v,
                        [f.name]: e.target.checked ? "true" : "false",
                      }))
                    }
                    className="mt-0.5 h-4 w-4 accent-accent"
                  />
                  <span>
                    {f.label}
                    {f.help_text && (
                      <span className="block text-[11px] text-foreground-muted">
                        {f.help_text}
                      </span>
                    )}
                  </span>
                </label>
              ) : (
                <label
                  key={f.name}
                  className="flex flex-col text-xs text-foreground-muted"
                >
                  {f.label}
                  <input
                    type="text"
                    data-cy={`mail-field-${f.name}`}
                    autoComplete="off"
                    spellCheck={false}
                    value={shown(f.name, "string")}
                    placeholder={f.placeholder}
                    onFocus={() => {
                      // A masked secret is not editable text. Clear it
                      // the moment the admin means to type, so nobody
                      // ends up editing bullets into a key.
                      if (f.secret && !(f.name in edited)) {
                        setEdited((v) => ({ ...v, [f.name]: "" }));
                      }
                    }}
                    onChange={(e) =>
                      setEdited((v) => ({ ...v, [f.name]: e.target.value }))
                    }
                    className="sk-input mt-1 font-mono w-auto"
                  />
                  {f.help_text && (
                    <span className="mt-1 text-[11px] text-foreground-muted">
                      {f.help_text}
                    </span>
                  )}
                </label>
              ),
            )}

            <label className="mt-1 flex items-start gap-2 text-sm text-foreground">
              <input
                type="checkbox"
                data-cy="mail-enabled"
                checked={enabled ?? false}
                onChange={(e) => setEnabled(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-accent"
              />
              <span>
                Send mail
                <span className="block text-[11px] text-foreground-muted">
                  Nothing is sent unless this is on AND the connection has
                  tested successfully.
                </span>
              </span>
            </label>

            <div className="mt-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                data-cy="btn-save-mail"
                disabled={save.isPending}
                onClick={() => save.mutate()}
                className="sk-btn sk-btn-primary"
              >
                {save.isPending ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                data-cy="btn-test-mail"
                disabled={test.isPending || dirty}
                title={
                  dirty
                    ? "Save first — the test uses the stored settings"
                    : "Ask Mailgun about the sending domain"
                }
                onClick={() => test.mutate()}
                className="sk-btn"
              >
                {test.isPending ? "Testing…" : "Test connection"}
              </button>
              {detail.data?.last_tested_at && !dirty && (
                <span className="text-xs text-foreground-muted">
                  {detail.data.last_test_succeeded
                    ? "Last test passed"
                    : `Last test failed — ${detail.data.last_test_error ?? "unknown"}`}
                </span>
              )}
            </div>
            <p className="text-[11px] text-foreground-muted">
              The test asks Mailgun about the sending domain; it does not
              send a message, so it can be run as often as you like.
            </p>
          </div>
        )}
      </section>

      {/* ---- who hears about a finished assessment ------------------ */}
      <section className="max-w-2xl rounded-xl border border-border bg-background-card p-5">
        <h3 className="text-sm font-semibold text-foreground">
          When a candidate finishes
        </h3>
        <p className="mt-1 text-xs text-foreground-muted">
          Who is told — whether the candidate submitted, ran out of time,
          or left early. Leave it empty to tell nobody.
        </p>
        <label className="sk-field mt-3 flex flex-col">
          <span className="sk-label">Recipients, comma separated</span>
          <textarea
            data-cy="mail-recipients"
            rows={3}
            value={recipients ?? ""}
            placeholder="lead.radiologist@clinic.example, practice.manager@clinic.example"
            onChange={(e) => setRecipients(e.target.value)}
            className="sk-input mt-1 w-auto"
          />
        </label>
        <button
          type="button"
          data-cy="btn-save-recipients"
          disabled={patchSettings.isPending || recipients === null}
          onClick={() => {
            void saveRecipients();
          }}
          className="sk-btn sk-btn-primary mt-3"
        >
          Save list
        </button>
      </section>
    </div>
  );
}
